import React from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle } from '@fortawesome/free-solid-svg-icons';
import theme from '../../styles/theme';

/** Light orangish palette for this section */
const orange = {
  bg: '#fff6ef',
  bgDeep: '#ffedd9',
  mesh:
    'linear-gradient(180deg, #fffaf6 0%, #fff3e8 45%, #ffe8d4 100%)',
  accent: '#ff8e53',
  accentSoft: '#ffa366',
  accentLight: '#ffc299',
  gradient: 'linear-gradient(135deg, #ffb07a 0%, #ff8e53 100%)',
  gradientIcon: 'linear-gradient(145deg, #ffc090 0%, #ff9548 100%)',
  border: 'rgba(255, 142, 83, 0.32)',
  borderSoft: 'rgba(255, 176, 122, 0.45)',
  textMuted: '#9a7b62',
  textBody: '#5c4a3a',
  glow: '0 6px 24px rgba(255, 142, 83, 0.2)',
  glowHover: '0 12px 36px rgba(255, 142, 83, 0.28)',
};

const WhySwitchSection = styled.section`
  padding: 72px 0 80px;
  background: ${orange.mesh};
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background:
      radial-gradient(ellipse 60% 50% at 10% 20%, rgba(255, 176, 122, 0.35) 0%, transparent 55%),
      radial-gradient(ellipse 55% 45% at 90% 70%, rgba(255, 142, 83, 0.28) 0%, transparent 50%),
      radial-gradient(ellipse 40% 30% at 50% 0%, rgba(255, 210, 170, 0.4) 0%, transparent 45%);
    pointer-events: none;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  position: relative;
  z-index: 1;
`;

const SectionTitle = styled.div`
  text-align: center;
  margin-bottom: 52px;

  h2 {
    font-size: clamp(1.75rem, 4vw, 2.25rem);
    color: ${theme.colors.text};
    margin-bottom: 16px;
    font-weight: 800;
    letter-spacing: -0.04em;

    span {
      color: ${orange.accent};
      background: none;
      -webkit-text-fill-color: ${orange.accent};
    }
  }

  p {
    font-size: ${theme.typography.body};
    color: ${orange.textMuted};
    max-width: 560px;
    margin: 0 auto;
    line-height: 1.6;
  }
`;

const BenefitsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 300px), 1fr));
  gap: 24px;
`;

const BenefitCard = styled.div`
  background: linear-gradient(160deg, #ffffff 0%, ${orange.bg} 55%, ${orange.bgDeep} 100%);
  padding: 28px 26px;
  border-radius: ${theme.radius.xl};
  border: 1px solid ${orange.borderSoft};
  box-shadow: ${orange.glow}, inset 0 1px 0 rgba(255, 255, 255, 0.9);
  transition: transform ${theme.transitions.base}, box-shadow ${theme.transitions.base},
    border-color ${theme.transitions.base};
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: ${orange.gradient};
  }

  &:hover {
    transform: translateY(-6px);
    box-shadow: ${orange.glowHover}, inset 0 1px 0 rgba(255, 255, 255, 0.95);
    border-color: ${orange.border};
  }
`;

const BenefitIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: ${theme.radius.md};
  background: ${orange.gradientIcon};
  color: white;
  font-size: 22px;
  margin-bottom: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 14px rgba(255, 142, 83, 0.35);
`;

const BenefitTitle = styled.h3`
  font-size: ${theme.typography.h3};
  color: #3d2f24;
  margin-bottom: 10px;
  font-weight: 700;
  letter-spacing: -0.02em;
`;

const BenefitDescription = styled.p`
  color: ${orange.textBody};
  line-height: 1.65;
  font-size: ${theme.typography.body};
  margin: 0;
`;

const benefits = [
  {
    title: 'All-in-One Solution',
    description:
      'Replace multiple tools with a single, powerful platform that handles all your marketing needs.',
  },
  {
    title: 'Cost-Effective',
    description: 'Save money by eliminating the need for multiple subscriptions and tools.',
  },
  {
    title: 'Time-Saving',
    description: 'Automate repetitive tasks and streamline your workflow for maximum efficiency.',
  },
  {
    title: 'Easy Integration',
    description: 'Seamlessly integrate with your existing tools and workflows without any hassle.',
  },
  {
    title: 'Scalable Growth',
    description: 'Grow your business with a platform that scales with your needs and ambitions.',
  },
  {
    title: '24/7 Support',
    description: 'Get expert support whenever you need it, with our dedicated customer success team.',
  },
];

const WhySwitch: React.FC = () => {
  return (
    <WhySwitchSection>
      <Container>
        <SectionTitle data-aos="fade-up">
          <h2>
            Why Make the <span>Switch?</span>
          </h2>
          <p>Discover why thousands of businesses trust our platform for their marketing needs.</p>
        </SectionTitle>
        <BenefitsGrid>
          {benefits.map((benefit, index) => (
            <BenefitCard key={index} data-aos="fade-up" data-aos-delay={index * 100}>
              <BenefitIcon>
                <FontAwesomeIcon icon={faCheckCircle} />
              </BenefitIcon>
              <BenefitTitle>{benefit.title}</BenefitTitle>
              <BenefitDescription>{benefit.description}</BenefitDescription>
            </BenefitCard>
          ))}
        </BenefitsGrid>
      </Container>
    </WhySwitchSection>
  );
};

export default WhySwitch;
